/**
 * Sort + filter + reflow para los paneles de la ficha — Fase 6b.
 *
 * Cada panel (.logbook-pilot-panel[data-panel]) tiene:
 *  - Una tabla con `<li class="logbook-stat-table-row">` cuyas filas
 *    llevan data-attributes con los valores crudos de cada columna
 *    (`data-date`, `data-title`, `data-aircraft`, etc.) — la fuente
 *    de verdad para ordenar y filtrar.
 *  - Un footer de paginación opcional con `data-pages`/`data-current`.
 *  - Un contenedor de filtros (`.logbook-panel-filters[data-filters]`)
 *    inicialmente vacío que `buildFiltersUI` rellena con los selects
 *    apropiados según el config del panel.
 *
 * Estado por panel (sort + filtros) vive en data-attributes del panel:
 * lo lee/escribe `reflowPanel` para reaplicar todo. Esto evita que se
 * pierda el estado al cambiar de tab y volver, dentro de la misma vida
 * de la página.
 */

import { escapeHtml } from './helpers.js';

/**
 * Fallback de filas/página cuando aún no podemos medir el DOM (raro:
 * solo si el body o las filas no tienen layout). El cálculo real lo
 * hace `computeRowsPerPage` midiendo el alto del body y de una fila.
 */
const FALLBACK_ROWS_PER_PAGE = 8;

/**
 * Filas que caben en el área visible del panel (body de la tabla)
 * dado el alto actual del cuaderno + tamaño de fila. Se recalcula al
 * mostrar un panel y al redimensionar la ventana, así la paginación
 * adapta al viewport en lugar de un valor fijo.
 */
function computeRowsPerPage(panel: HTMLElement): number {
  const body = panelContext(panel).querySelector<HTMLElement>(
    '.logbook-stat-table-body',
  );
  if (!body) return FALLBACK_ROWS_PER_PAGE;
  // Buscamos una fila visible (no [hidden]) para que offsetHeight sea
  // distinto de 0; si no hay ninguna, caemos al fallback.
  const sampleRow =
    body.querySelector<HTMLElement>(
      '.logbook-stat-table-row:not([hidden])',
    ) ?? body.querySelector<HTMLElement>('.logbook-stat-table-row');
  if (!sampleRow) return FALLBACK_ROWS_PER_PAGE;
  const rowHeight = sampleRow.offsetHeight;
  if (rowHeight === 0) return FALLBACK_ROWS_PER_PAGE;
  const available = body.clientHeight;
  if (available === 0) return FALLBACK_ROWS_PER_PAGE;
  return Math.max(1, Math.floor(available / rowHeight));
}

/** Identificadores de panel (mismo set que en markup-piloto.ts). */
type PanelId = 'missions' | 'kills' | 'ground' | 'deaths' | 'hours' | 'reports';

/**
 * Cada filtro de un panel. `kind:date` se renderiza como 6 selects
 * (día/mes/año inicio + fin). `kind:enum` como 1 select con los valores
 * únicos de la columna en el dataset.
 */
type FilterField =
  | { kind: 'date'; col: 'date'; label: string }
  | { kind: 'enum'; col: string; label: string };

const FILTER_CONFIG: Record<PanelId, FilterField[]> = {
  missions: [
    { kind: 'date', col: 'date', label: 'FECHA' },
    { kind: 'enum', col: 'title', label: 'MISIÓN' },
    { kind: 'enum', col: 'aircraft', label: 'AERONAVE' },
  ],
  kills: [
    { kind: 'date', col: 'date', label: 'FECHA' },
    { kind: 'enum', col: 'title', label: 'MISIÓN' },
    { kind: 'enum', col: 'target', label: 'AVIÓN' },
    { kind: 'enum', col: 'weapon', label: 'ARMAMENTO' },
  ],
  ground: [
    { kind: 'date', col: 'date', label: 'FECHA' },
    { kind: 'enum', col: 'title', label: 'MISIÓN' },
    { kind: 'enum', col: 'target', label: 'UNIDAD' },
    { kind: 'enum', col: 'weapon', label: 'ARMAMENTO' },
  ],
  deaths: [
    { kind: 'date', col: 'date', label: 'FECHA' },
    { kind: 'enum', col: 'title', label: 'MISIÓN' },
    { kind: 'enum', col: 'aircraft', label: 'AERONAVE' },
    { kind: 'enum', col: 'killedby', label: 'DESTRUIDO POR' },
  ],
  hours: [
    { kind: 'date', col: 'date', label: 'FECHA' },
    { kind: 'enum', col: 'title', label: 'MISIÓN' },
  ],
  reports: [
    { kind: 'date', col: 'date', label: 'FECHA' },
    { kind: 'enum', col: 'title', label: 'MISIÓN' },
  ],
};

/**
 * Devuelve el panel actualmente visible (el que NO tiene atributo
 * `hidden`). Si por alguna razón no hay ninguno o hay varios, devuelve
 * el primero — el resto del sistema asume que solo uno está visible.
 */
function findActivePanel(): HTMLElement | null {
  const all = document.querySelectorAll<HTMLElement>(
    '.logbook-pilot-panel[data-panel]',
  );
  for (const p of all) {
    if (!p.hasAttribute('hidden')) return p;
  }
  return null;
}

/**
 * Construye/reconstruye la UI de filtros del sidebar para el panel
 * activo. Llamar al montar la ficha y CADA VEZ que cambia el panel
 * (desde `mount-piloto.ts → activatePanel`). Lee las filas del panel
 * activo para sacar valores únicos por columna y años de las fechas.
 */
export function buildFiltersUI(): void {
  const sidebarContainer = document.querySelector<HTMLElement>(
    '[data-filters-sidebar] [data-filters]',
  );
  if (!sidebarContainer) return;
  const panel = findActivePanel();
  if (!panel) {
    sidebarContainer.innerHTML = '';
    return;
  }
  const panelId = panel.dataset['panel'] as PanelId | undefined;
  if (!panelId || !(panelId in FILTER_CONFIG)) {
    sidebarContainer.innerHTML = '';
    return;
  }
  const rows = panelContext(panel).querySelectorAll<HTMLElement>(
    '.logbook-stat-table-row',
  );
  sidebarContainer.innerHTML = renderFilterFields(panelId, rows);
}

/**
 * Devuelve el contexto efectivo de un panel. En paneles simples es el
 * propio `<section>`. En el panel `reports` el contenido se divide en
 * 3 sub-tablas (REPORTE/BRIEFING/DEBRIEFING) bajo `[data-report-subkind]`,
 * de las que solo una está visible (`:not([hidden])`); el resto del
 * módulo opera sobre esa sub-tabla activa, no sobre las 3 a la vez.
 *
 * Si no hay sub-tablas (paneles missions/kills/...) o todas están
 * ocultas (estado imposible en este UI pero defensivo), cae al panel.
 */
function panelContext(panel: HTMLElement): HTMLElement {
  const sub = panel.querySelector<HTMLElement>(
    '[data-report-subkind]:not([hidden])',
  );
  return sub ?? panel;
}

function renderFilterFields(
  panelId: PanelId,
  rows: NodeListOf<HTMLElement>,
): string {
  const config = FILTER_CONFIG[panelId];
  const fieldsHtml = config
    .map((f) => {
      if (f.kind === 'date') return renderDateFilter(rows);
      return renderEnumFilter(f, rows);
    })
    .join('');
  const total = rows.length;
  return `
    ${fieldsHtml}
    <div class="logbook-filters-actions">
      <span class="logbook-filters-count" data-filters-count>
        MOSTRANDO <span data-filters-visible>${total}</span>/<span data-filters-total>${total}</span>
      </span>
      <button type="button" class="logbook-filters-clear" data-filter-action="clear">LIMPIAR</button>
    </div>
  `;
}

function renderEnumFilter(
  field: { col: string; label: string },
  rows: NodeListOf<HTMLElement>,
): string {
  const values = new Set<string>();
  rows.forEach((r) => {
    const v = r.dataset[field.col];
    if (v) values.add(v);
  });
  const sorted = Array.from(values).sort((a, b) => a.localeCompare(b));
  const opts = sorted
    .map((v) => `<option value="${escapeHtml(v)}">${escapeHtml(v)}</option>`)
    .join('');
  return `
    <div class="logbook-filter-field">
      <label>${escapeHtml(field.label)}</label>
      <select data-filter-col="${escapeHtml(field.col)}">
        <option value="">— todos —</option>
        ${opts}
      </select>
    </div>
  `;
}

function renderDateFilter(rows: NodeListOf<HTMLElement>): string {
  // Extraer años únicos del dataset (data-date = ISO timestamp).
  const years = new Set<number>();
  rows.forEach((r) => {
    const iso = r.dataset['date'];
    if (!iso) return;
    const d = new Date(iso);
    if (!Number.isNaN(d.getTime())) years.add(d.getFullYear());
  });
  const sortedYears = Array.from(years).sort((a, b) => b - a);

  const dayOpts = numericOptions(1, 31);
  const monthOpts = numericOptions(1, 12);
  const yearOpts = sortedYears
    .map((y) => `<option value="${y}">${y}</option>`)
    .join('');

  // Bloque inicio.
  const startBlock = `
    <div class="logbook-filter-date-row">
      <span class="logbook-filter-date-label">INICIO</span>
      <select data-filter-date="startDay" aria-label="Día inicio">
        <option value="">DD</option>${dayOpts}
      </select>
      <select data-filter-date="startMonth" aria-label="Mes inicio">
        <option value="">MM</option>${monthOpts}
      </select>
      <select data-filter-date="startYear" aria-label="Año inicio">
        <option value="">AAAA</option>${yearOpts}
      </select>
    </div>`;

  // Bloque fin.
  const endBlock = `
    <div class="logbook-filter-date-row">
      <span class="logbook-filter-date-label">FIN</span>
      <select data-filter-date="endDay" aria-label="Día fin">
        <option value="">DD</option>${dayOpts}
      </select>
      <select data-filter-date="endMonth" aria-label="Mes fin">
        <option value="">MM</option>${monthOpts}
      </select>
      <select data-filter-date="endYear" aria-label="Año fin">
        <option value="">AAAA</option>${yearOpts}
      </select>
    </div>`;

  return `
    <div class="logbook-filter-field logbook-filter-field--date">
      <label>FECHA</label>
      ${startBlock}
      ${endBlock}
    </div>
  `;
}

function numericOptions(from: number, to: number): string {
  const out: string[] = [];
  for (let i = from; i <= to; i++) {
    const s = String(i).padStart(2, '0');
    out.push(`<option value="${i}">${s}</option>`);
  }
  return out.join('');
}

/**
 * Toggle de la sección de filtros en el sidebar. Actualiza aria-pressed
 * del botón FILTROS (visual feedback) y muestra/oculta la `<section>`
 * de filtros que vive bajo ACCIONES.
 */
export function toggleFilters(button: HTMLButtonElement): void {
  const isOpen = button.getAttribute('aria-pressed') === 'true';
  const next = !isOpen;
  button.setAttribute('aria-pressed', next ? 'true' : 'false');
  const sidebar = document.querySelector<HTMLElement>(
    '[data-filters-sidebar]',
  );
  if (!sidebar) return;
  if (next) sidebar.removeAttribute('hidden');
  else sidebar.setAttribute('hidden', '');
}

/**
 * Cablea handlers de sort + filtros. Sort vive en los headers de las
 * tablas (dentro de cada panel); filtros viven en el sidebar (un solo
 * contenedor). Cada cambio dispara `reflowPanel()` sobre el panel
 * actualmente visible.
 */
/**
 * Cablea un listener de resize (debounced) que reaplica la paginación
 * al panel activo. Se llama una sola vez al montar la ficha.
 */
export function wireResizeReflow(): void {
  let scheduled = 0;
  window.addEventListener('resize', () => {
    if (scheduled) cancelAnimationFrame(scheduled);
    scheduled = requestAnimationFrame(() => {
      scheduled = 0;
      refreshActivePanel();
    });
  });
}

export function wirePanelControls(): void {
  // Sort: click en header → cicla none/asc/desc.
  document.addEventListener('click', (ev) => {
    const target = ev.target as HTMLElement;
    const sortBtn = target.closest<HTMLButtonElement>(
      '.logbook-sort-btn[data-sort-col]',
    );
    if (sortBtn) {
      onSortClick(sortBtn);
      return;
    }
    if (target.dataset['filterAction'] === 'clear') {
      const container = target.closest<HTMLElement>('[data-filters]');
      if (container) clearFilters(container);
    }
  });

  // Filtros (en el sidebar): cambio en cualquier select → reflow al
  // panel activo. Los selects ya no viven dentro del panel, así que
  // hay que buscar el panel visible explícitamente.
  document.addEventListener('change', (ev) => {
    const target = ev.target as HTMLElement;
    if (!target.matches('[data-filter-col], [data-filter-date]')) return;
    const panel = findActivePanel();
    if (panel) reflowPanel(panel);
  });
}

function onSortClick(btn: HTMLButtonElement): void {
  const col = btn.dataset['sortCol'];
  if (!col) return;
  const table = btn.closest<HTMLElement>('.logbook-stat-table');
  const panel = btn.closest<HTMLElement>('.logbook-pilot-panel');
  if (!table || !panel) return;
  const currentCol = table.dataset['sortCol'] ?? '';
  const currentDir = table.dataset['sortDir'] ?? '';

  // Cycle: ningún sort → asc; mismo col asc → desc; mismo col desc → ninguno.
  let nextDir: '' | 'asc' | 'desc';
  if (currentCol !== col) nextDir = 'asc';
  else if (currentDir === 'asc') nextDir = 'desc';
  else if (currentDir === 'desc') nextDir = '';
  else nextDir = 'asc';

  if (nextDir === '') {
    delete table.dataset['sortCol'];
    delete table.dataset['sortDir'];
  } else {
    table.dataset['sortCol'] = col;
    table.dataset['sortDir'] = nextDir;
  }

  reflowPanel(panel);
}

/** Limpia todos los selects del contenedor y reaplica reflow al panel activo. */
function clearFilters(container: HTMLElement): void {
  container
    .querySelectorAll<HTMLSelectElement>('select')
    .forEach((s) => (s.value = ''));
  const panel = findActivePanel();
  if (panel) reflowPanel(panel);
}

interface FilterValues {
  /** col → valor seleccionado del select enum. */
  enums: Map<string, string>;
  /** Fecha inicio (cualquier campo puede ser NaN si está vacío). */
  startY?: number;
  startM?: number;
  startD?: number;
  /** Fecha fin. */
  endY?: number;
  endM?: number;
  endD?: number;
}

function readFilters(_panel: HTMLElement): FilterValues {
  // El contenedor de filtros vive en el sidebar (no dentro del panel).
  // Lo buscamos directamente en document; si no existe (edge case),
  // devolvemos filtros vacíos.
  const container = document.querySelector<HTMLElement>(
    '[data-filters-sidebar] [data-filters]',
  );
  const values: FilterValues = { enums: new Map() };
  if (!container) return values;
  container
    .querySelectorAll<HTMLSelectElement>('[data-filter-col]')
    .forEach((s) => {
      if (s.value) values.enums.set(s.dataset['filterCol']!, s.value);
    });
  container
    .querySelectorAll<HTMLSelectElement>('[data-filter-date]')
    .forEach((s) => {
      if (!s.value) return;
      const key = s.dataset['filterDate']!;
      const n = Number(s.value);
      if (Number.isNaN(n)) return;
      switch (key) {
        case 'startDay':
          values.startD = n;
          break;
        case 'startMonth':
          values.startM = n;
          break;
        case 'startYear':
          values.startY = n;
          break;
        case 'endDay':
          values.endD = n;
          break;
        case 'endMonth':
          values.endM = n;
          break;
        case 'endYear':
          values.endY = n;
          break;
      }
    });
  return values;
}

/**
 * Comprueba si la fila pasa todos los filtros del panel. Filtros enum:
 * el valor del data-attribute debe coincidir exactamente. Filtros de
 * fecha: se construye un rango [start, end] interpretando los campos
 * vacíos como sin restricción; la fecha de la fila debe caer dentro.
 */
function rowPassesFilters(row: HTMLElement, filters: FilterValues): boolean {
  // Enum filters.
  for (const [col, expected] of filters.enums) {
    if (row.dataset[col] !== expected) return false;
  }
  // Date filter (solo si hay al menos un campo de fecha relleno).
  const hasDateFilter =
    filters.startY !== undefined ||
    filters.startM !== undefined ||
    filters.startD !== undefined ||
    filters.endY !== undefined ||
    filters.endM !== undefined ||
    filters.endD !== undefined;
  if (!hasDateFilter) return true;

  const iso = row.dataset['date'];
  if (!iso) return false;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return false;

  // Comparación parte a parte: si el filtro tiene año, comparar año; si
  // tiene mes, comparar (año, mes); si tiene día, comparar fecha completa.
  // Faltantes se rellenan con extremos (1 ene / 31 dic) según borde.
  const start = buildBoundary(
    filters.startY,
    filters.startM,
    filters.startD,
    'start',
  );
  const end = buildBoundary(filters.endY, filters.endM, filters.endD, 'end');

  if (start && d < start) return false;
  if (end && d > end) return false;
  return true;
}

function buildBoundary(
  y: number | undefined,
  m: number | undefined,
  day: number | undefined,
  edge: 'start' | 'end',
): Date | null {
  // Si TODOS son undefined, no hay frontera por este lado.
  if (y === undefined && m === undefined && day === undefined) return null;
  // Si solo año está definido → 1 ene 00:00 (start) / 31 dic 23:59:59 (end).
  // Si año+mes → 1 del mes / último día del mes.
  // Si año+mes+día → ese día (00:00 / 23:59:59).
  // Si NO hay año, no podemos construir una frontera coherente —
  // ignoramos (devolver null).
  if (y === undefined) return null;
  let actualMonth: number;
  let actualDay: number;
  let h = 0;
  let min = 0;
  let s = 0;
  let ms = 0;
  if (edge === 'start') {
    actualMonth = m ?? 1;
    actualDay = day ?? 1;
  } else {
    actualMonth = m ?? 12;
    if (day !== undefined) {
      actualDay = day;
    } else {
      // último día del mes
      actualDay = new Date(y, actualMonth, 0).getDate();
    }
    h = 23;
    min = 59;
    s = 59;
    ms = 999;
  }
  return new Date(y, actualMonth - 1, actualDay, h, min, s, ms);
}

/**
 * Reaplica filtro/sort/paginación al panel ACTIVO (visible). Llamar
 * tras montar la ficha, al cambiar de panel y en resize de ventana —
 * así rows-per-page adapta al alto de la hoja en cada caso.
 */
export function refreshActivePanel(): void {
  const panel = findActivePanel();
  if (panel) reflowPanel(panel);
}

/**
 * Recalcula visibilidad de filas + orden + paginación basado en el
 * estado actual del panel (sort en table.dataset, filtros en los selects).
 */
function reflowPanel(panel: HTMLElement): void {
  const ctx = panelContext(panel);
  const table = ctx.querySelector<HTMLElement>('.logbook-stat-table');
  const body = ctx.querySelector<HTMLElement>('.logbook-stat-table-body');
  const pag = ctx.querySelector<HTMLElement>('.logbook-stat-pagination');
  if (!table || !body) return;

  const allRows = Array.from(
    body.querySelectorAll<HTMLElement>(':scope > .logbook-stat-table-row'),
  );

  // 1. Filtrado.
  const filters = readFilters(panel);
  const visible = allRows.filter((r) => rowPassesFilters(r, filters));

  // Actualizar el contador del sidebar para que el usuario vea el efecto
  // del filtro aunque el panel solo muestre una página.
  const countVisible = document.querySelector<HTMLElement>(
    '[data-filters-sidebar] [data-filters-visible]',
  );
  const countTotal = document.querySelector<HTMLElement>(
    '[data-filters-sidebar] [data-filters-total]',
  );
  if (countVisible) countVisible.textContent = String(visible.length);
  if (countTotal) countTotal.textContent = String(allRows.length);

  // 2. Orden.
  const sortCol = table.dataset['sortCol'] ?? '';
  const sortDir = table.dataset['sortDir'] ?? '';
  if (sortCol && (sortDir === 'asc' || sortDir === 'desc')) {
    visible.sort((a, b) => compareRows(a, b, sortCol, sortDir));
    for (const r of visible) body.appendChild(r);
  }

  // 3. Indicadores de sort en los headers.
  refreshSortIndicators(table, sortCol, sortDir);

  // 4. Repaginación: data-page se reconstruye sobre las filas visibles.
  // El número de filas/página se mide dinámicamente del DOM para que
  // la tabla llene el alto disponible de la hoja sin recortar antes.
  const rowsPerPage = computeRowsPerPage(panel);
  let i = 0;
  for (const r of allRows) {
    const isVisible = visible.includes(r);
    if (!isVisible) {
      r.dataset['page'] = '0'; // jamás coincide con 1..N → permanece hidden
      continue;
    }
    const page = Math.floor(i / rowsPerPage) + 1;
    r.dataset['page'] = String(page);
    i++;
  }

  // 5. Actualizar el footer de paginación si existe.
  if (pag) {
    const totalPages = Math.max(1, Math.ceil(visible.length / rowsPerPage));
    pag.dataset['pages'] = String(totalPages);
    const total = pag.querySelector<HTMLElement>('[data-pagination-total]');
    if (total) total.textContent = String(totalPages);
    if (totalPages <= 1) {
      pag.setAttribute('hidden', '');
    } else {
      pag.removeAttribute('hidden');
    }
    // Reset a página 1 (la única coherente tras filtro/sort).
    pag.dataset['current'] = '1';
    const cur = pag.querySelector<HTMLElement>('[data-pagination-current]');
    if (cur) cur.textContent = '1';
    const prev = pag.querySelector<HTMLButtonElement>(
      '[data-paginate="prev"]',
    );
    const next = pag.querySelector<HTMLButtonElement>(
      '[data-paginate="next"]',
    );
    if (prev) prev.disabled = true;
    if (next) next.disabled = totalPages <= 1;
  }

  // 6. Aplicar visibilidad por página (página 1).
  for (const r of allRows) {
    if (r.dataset['page'] === '1') {
      r.removeAttribute('hidden');
    } else {
      r.setAttribute('hidden', '');
    }
  }
}

function compareRows(
  a: HTMLElement,
  b: HTMLElement,
  col: string,
  dir: 'asc' | 'desc',
): number {
  const va = a.dataset[col] ?? '';
  const vb = b.dataset[col] ?? '';
  // Si los dos parecen números, comparación numérica; si parecen ISO
  // timestamps, son chronologically ordered as strings; resto, string.
  let cmp: number;
  const na = Number(va);
  const nb = Number(vb);
  if (!Number.isNaN(na) && !Number.isNaN(nb) && va !== '' && vb !== '') {
    cmp = na - nb;
  } else {
    cmp = va.localeCompare(vb);
  }
  return dir === 'asc' ? cmp : -cmp;
}

function refreshSortIndicators(
  table: HTMLElement,
  col: string,
  dir: string,
): void {
  table
    .querySelectorAll<HTMLElement>('.logbook-sort-arrow')
    .forEach((arrow) => (arrow.textContent = '⇅'));
  if (!col || !dir) return;
  const activeBtn = table.querySelector<HTMLElement>(
    `.logbook-sort-btn[data-sort-col="${col}"]`,
  );
  const arrow = activeBtn?.querySelector<HTMLElement>('.logbook-sort-arrow');
  if (arrow) arrow.textContent = dir === 'asc' ? '▲' : '▼';
}
